import { find } from 'lodash';
import React, { ComponentType, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Outlet,
    Route,
    RouterProvider,
    createBrowserRouter,
    createRoutesFromElements,
    useRouteError
} from 'react-router-dom';
import { authRoutes } from '../../authRoutes';
import { PlanningSchedulerDataProvider } from '../../scheduler/PlanningSchedulerDataContext';
import { ReservationSchedulerDataProvider } from '../../supply/dashboard/Timeline/ReservationSchedulerDataContext';
import { ErrorFallback } from '../ErrorBoundary';
import SecureAttachmentDownload from '../auth/SecureAttachmentDownload';
import { UserContext } from '../auth/UserContext';
import { Membership, UserProfile, fetchUserProfile, getNewToken, isTokenExpired } from '../auth/api';
import Loading from '../components/Loading';
import { ROUTE_CONSTANTS, SECURE_ATTACHMENT_DOWNLOAD_ROUTE_PATH } from '../constants/constants';
import { DEEP_LINK_RESOLVER_ROUTE_PATH } from '../deepLink/constants';
import DeepLinkResolver from '../deepLink/ui/components/DeepLinkResolver';
import { log } from '../utils/commonUtils';
import useHistory, { ChangeRoute } from '../utils/useHistory';
import AppRoute, { RouteType } from './AppRoute';
import { LIGHTING_PROFESSIONAL, PLANNING, PRODUCTION_CONNECT, PRODUCTION_PROFESSIONAL, SUPPLIER } from './apps';
import useDefaultRoute from './useDefaultRoute';

/**
 * @todo: This really isn't an ErrorBoundary, it's just a common error message display component.
 * Review naming and usage to disambiguate.
 */
const ErrorBoundary = () => {
    const error = useRouteError();
    return <ErrorFallback error={error as Error} />;
};

const AppProviderShell = ({ Provider }) => (
    <Provider>
        <Outlet />
    </Provider>
);

/**
 * @todo
 * 1. This function is used like a Component. Review naming and usage to disambiguate.
 * 2. The value that's getting drilled down in to the `allRoutes` parameter can perhaps be extracted as a constant.
 */
const addRoutes = (allRoutes, children = undefined) => {
    let routesAndRedirects = [];
    (children || allRoutes).forEach((route: RouteType) => {
        if (route.path) {
            // If component is defined it will be rendered, otherwise if redirect is defined we will redirect to it.
            if (route.component) {
                routesAndRedirects.push(
                    <Route
                        errorElement={<ErrorBoundary />}
                        path={route.path}
                        element={<AppRoute routes={allRoutes} {...route} />}
                    />
                );
            } else if (route.redirect) {
                routesAndRedirects.push(<Route path={route.path} element={<ChangeRoute path={route.redirect} />} />);
            }
        }
        if (route.items) {
            const childrenRoutes = addRoutes(allRoutes, route.items);
            routesAndRedirects = routesAndRedirects.concat(childrenRoutes);
        }
    });
    return routesAndRedirects;
};

const addAppScopedRoutes = (
    allRoutes: RouteType[],
    appScopedProviders: Map<string, ComponentType<PropsWithChildren>>
) => {
    const groupedByApp = new Map<string, RouteType[]>();
    const remainingRoutes: RouteType[] = [];

    for (const route of allRoutes) {
        const appId = route.app?.id;
        if (appId && appScopedProviders.has(appId)) {
            const list = groupedByApp.get(appId);
            if (list) {
                list.push(route);
            } else {
                groupedByApp.set(appId, [route]);
            }
        } else {
            remainingRoutes.push(route);
        }
    }

    const providerRoutes = Array.from(groupedByApp.entries()).map(([appId, routesForApp]) => {
        const Provider = appScopedProviders.get(appId)!;
        return (
            <Route key={appId} element={<AppProviderShell Provider={Provider} />}>
                {addRoutes(allRoutes, routesForApp)}
            </Route>
        );
    });

    return [...providerRoutes, ...addRoutes(allRoutes, remainingRoutes)];
};

const getDefaultOrgId = (userProfile: UserProfile) => {
    // User not subscribed to any accounts
    if (userProfile.memberships?.length === 1 && userProfile.memberships[0].personal === true) {
        return userProfile.memberships[0].id;
    } else {
        // Find membership by id or first non-personal one.
        const membership = find(userProfile.memberships, (m: Membership) => {
            return !m.personal;
        });
        return membership?.id;
    }
};

const DefaultRoute = () => {
    const { isAuthenticated, userProfile } = useContext(UserContext);
    const { searchParams } = useHistory();
    const orgId = searchParams.get('orgId');
    const { url } = useDefaultRoute();

    if (!isAuthenticated) {
        log('DefaultRoute: User not authenticated, redirecting to /login');
        return <ChangeRoute path={'/login'} />;
    } else if (isAuthenticated && userProfile && !orgId) {
        log('DefaultRoute: User profile present, redirecting to default url', url);
        return <ChangeRoute path={url} />;
    } else if (!userProfile) {
        log('DefaultRoute: User profile missing, redirecting to /login');
        return <ChangeRoute path={'/login'} />;
    } else if (!orgId) {
        log('DefaultRoute: orgId missing in the url, redirecting to /login');
        return <ChangeRoute path={'/login'} />;
    } else if (!url) {
        log('DefaultRoute: Failed to find default route, redirecting to /login');
        return <ChangeRoute path={'/login'} />;
    } else {
        log('DefaultRoute: User profile present, redirecting to default url', url);
        return <ChangeRoute path={url} />;
    }
};

export default function AppRouter({ routes }) {
    const [refreshDone, setRefreshDone] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { setIsAuthenticated, setActiveOrganizationAccount, setUserProfile } = useContext(UserContext);

    /**
     * Fetch user profile and set it in UserContext, also set the activeOrganizationAccount
     * to personal account. If the user is authenticated redirect to profile
     * page.
     *
     * @todo: Move it inside the `useEffect` below, as that's the only place it's used.
     */
    const getUserProfile = useCallback(
        (idFromLocalStorage = '') => {
            fetchUserProfile(idFromLocalStorage, (data) => {
                setUserProfile(data);
                setActiveOrganizationAccount(idFromLocalStorage || data.memberships[0].id, true);
            }).then(() => {
                setIsLoading(false);
            });
        },
        [setActiveOrganizationAccount, setUserProfile]
    );

    /**
     * @todo: Update dependency array to avoid exhaustive-deps warning.
     */
    useEffect(() => {
        if (!refreshDone) {
            getNewToken()
                .then(() => {
                    const expired = isTokenExpired();
                    log('AppRouter: Token refresh done, is token expired?', expired);
                    const idFromLocalStorage = localStorage.getItem(ROUTE_CONSTANTS.lastSelectedOrgId);
                    if (!expired) {
                        setIsLoading(true);
                        getUserProfile(idFromLocalStorage);
                    }
                })
                .finally(() => {
                    const expired = isTokenExpired();
                    setRefreshDone(true);
                    setIsAuthenticated(!expired);
                });
        }
    }, [getUserProfile, refreshDone, setIsAuthenticated]);

    const authorizationRoutes = useMemo(() => {
        return authRoutes.map((route) => <Route path={route.path} element={route.component} />);
    }, []);

    const router = useMemo(() => {
        const appScopedProviders = new Map<string, ComponentType<PropsWithChildren>>([
            [SUPPLIER.id, ReservationSchedulerDataProvider],
            [PLANNING.id, PlanningSchedulerDataProvider],
            [PRODUCTION_PROFESSIONAL.id, PlanningSchedulerDataProvider],
            [LIGHTING_PROFESSIONAL.id, PlanningSchedulerDataProvider],
            [PRODUCTION_CONNECT.id, PlanningSchedulerDataProvider]
        ]);
        const routeElements = [
            ...authorizationRoutes,
            <Route path={DEEP_LINK_RESOLVER_ROUTE_PATH} element={<DeepLinkResolver />} />,
            <Route path={SECURE_ATTACHMENT_DOWNLOAD_ROUTE_PATH} element={<SecureAttachmentDownload />} />,
            ...addAppScopedRoutes(routes, appScopedProviders),
            <Route path="*" element={<DefaultRoute />} />
        ];
        return createBrowserRouter(createRoutesFromElements(routeElements));
    }, [authorizationRoutes, routes]);

    if (isLoading) {
        return <Loading />;
    }
    return refreshDone ? <RouterProvider router={router} fallbackElement={<Loading />} /> : <Loading />;
}
